import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useHasPrivilege } from "../../../hooks/usePermissions";
import s from "./Events.module.css";
import commonStyles from '../../common/CommonStyles.module.css';

const Events = () => {
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [events, setEvents] = useState([]);
  const [userEvents, setUserEvents] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [eventSearch, setEventSearch] = useState("");
  const [foundEvents, setFoundEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedEventName, setSelectedEventName] = useState(""); // Добавляем имя выбранного события
  const [userEventDate, setUserEventDate] = useState("");
  const hasPrivilege = useHasPrivilege();
  const navigate = useNavigate();

  // Загрузка всех событий
  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await fetch("/api/events");
      if (!response.ok) throw new Error("Ошибка при загрузке событий");
      const data = await response.json();
      setEvents(data);
    } catch (error) {
      console.error("Error fetching events:", error);
      setErrorMessage("Ошибка при загрузке событий");
    }
  };

  // Поиск пользователей
  const fetchUsers = async () => {
    if (searchQuery.length < 2) {
      setUsers([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/users/search?query=${encodeURIComponent(searchQuery)}`);
      if (!response.ok) throw new Error("Ошибка при загрузке пользователей");
      const data = await response.json();
      setUsers(data.content || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      setErrorMessage("Ошибка при загрузке пользователей");
    } finally {
      setLoading(false);
    }
  };

  // Поиск событий
  useEffect(() => {
    if (eventSearch.length < 2) {
      setFoundEvents([]);
      return;
    }

    const searchTimeout = setTimeout(async () => {
      try {
        const response = await fetch(`/api/events/search?query=${encodeURIComponent(eventSearch)}`);

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Ошибка при поиске событий");
        }

        const data = await response.json();
        setFoundEvents(data.content || data);
      } catch (error) {
        console.error("Error searching events:", error);
        setErrorMessage(error.message || "Ошибка при поиске событий");
        setFoundEvents([]);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [eventSearch]);

  // Загрузка событий пользователя
  const fetchUserEvents = async (userId) => {
    try {
      const response = await fetch(`/api/user-events/${userId}`);
      if (!response.ok) throw new Error("Ошибка при загрузке событий пользователя");
      const data = await response.json();
      setUserEvents(data);
    } catch (error) {
      console.error("Error fetching user events:", error);
      setErrorMessage("Ошибка при загрузке событий пользователя");
    }
  };

  // Обработка поиска
  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setSelectedUser(null);
  };

  // Обработка выбора пользователя
  const handleUserClick = (user) => {
    setSelectedUser(user);
    fetchUserEvents(user.id);
  };

  // Обработка выбора события
  const handleEventSelect = (event) => {
    setSelectedEventId(event.id);
    setSelectedEventName(event.eventName);
    setEventSearch(""); // Сбрасываем поиск
    setFoundEvents([]); // Очищаем результаты поиска
  };

  // Отмена выбора события
  const handleClearEventSelection = () => {
    setSelectedEventId(null);
    setSelectedEventName("");
    setUserEventDate("");
  };

  // Добавление события пользователю
  const handleAddEventToUser = async () => {
    if (!selectedUser || !selectedEventId || !userEventDate) {
      setErrorMessage("Выберите событие и укажите дату");
      return;
    }

    try {
      const response = await fetch("/api/user-events", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: selectedUser.id,
          eventId: selectedEventId,
          eventDate: userEventDate,
        }),
      });
      if (!response.ok) throw new Error("Ошибка при добавлении события");
      const newEvent = await response.json();
      setUserEvents((prev) => [...prev, newEvent]);
      handleClearEventSelection(); // Сбрасываем выбор после добавления
    } catch (error) {
      console.error("Error adding event:", error);
      setErrorMessage("Ошибка при добавлении события");
    }
  };

  // Удаление события пользователя
  const handleDeleteUserEvent = async (eventId) => {
    if (!window.confirm("Удалить событие?")) return;

    try {
      const response = await fetch(`/api/user-events/${eventId}`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error("Ошибка при удалении события");
      setUserEvents((prev) => prev.filter((e) => e.id !== eventId));
    } catch (error) {
      console.error("Error deleting event:", error);
      setErrorMessage("Ошибка при удалении события");
    }
  };

  // Переход на страницу управления событиями
  const handleManageEvents = () => {
    navigate("/admin/manage-events");
  };

  // Закрытие модального окна
  const handleCloseModal = () => {
    setErrorMessage("");
    setSelectedUser(null);
    handleClearEventSelection(); // Сбрасываем выбор события при закрытии
  };

  return (
      <div className={commonStyles.pageContainer}>
        <div className={commonStyles.contentContainer}>
          <div className={commonStyles.content}>
            <h2 style={{ color: 'rgb(151, 1, 14)', textAlign: 'center', fontFamily: 'Ysabeau SC, sans-serif' }}>
              Управление событиями
            </h2>
            {hasPrivilege("MANAGE:EVENTS") && (
                <button
                    style={{ backgroundColor: 'rgb(151, 1, 14)', color: 'white', borderRadius: 4, fontWeight: 500, padding: '0.8rem 1.5rem', fontSize: '1rem', marginBottom: 24 }}
                    onClick={handleManageEvents}
                >
                  Управление событиями
                </button>
            )}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
              <input
                  type="text"
                  placeholder="Поиск по ФИО пользователя..."
                  style={{ flex: 1, minWidth: 220, maxWidth: 400, padding: '0.8rem', border: '2px solid #e0e0e0', borderRadius: 4, fontSize: '1rem' }}
                  value={searchQuery}
                  onChange={handleSearch}
                  onKeyUp={(e) => e.key === "Enter" && fetchUsers()}
              />
              <button
                  style={{ backgroundColor: '#2196F3', color: 'white', borderRadius: 4, fontWeight: 500, padding: '0.8rem 1.5rem', fontSize: '1rem' }}
                  onClick={fetchUsers}
              >
                Найти
              </button>
            </div>
            {loading ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'rgb(151, 1, 14)' }}>Загрузка...</div>
            ) : users.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20, marginTop: 20 }}>
                  {users.map((user) => (
                      <div
                          key={user.id}
                          style={{
                            background: selectedUser?.id === user.id ? '#f0f8ff' : 'white',
                            padding: 20,
                            borderRadius: 8,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            cursor: 'pointer',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            border: selectedUser?.id === user.id ? '2px solid #2196F3' : '2px solid transparent'
                          }}
                          onClick={() => handleUserClick(user)}
                      >
                        <h3 style={{ margin: '0 0 10px', color: '#2c3e50' }}>{user.firstName} {user.lastName}</h3>
                        <p style={{ margin: 0, color: '#666' }}>{user.email}</p>
                      </div>
                  ))}
                </div>
            ) : (
                <div style={{ textAlign: 'center', padding: 40, color: '#999', fontSize: 18 }}>Пользователи не найдены</div>
            )}

            {/* Модальное окно для управления событиями пользователя */}
            {selectedUser && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ background: 'white', padding: '2rem', borderRadius: 8, width: '90%', maxWidth: 600 }}>
                    <h3 style={{ color: 'rgb(151, 1, 14)', textAlign: 'center', fontFamily: 'Ysabeau SC, sans-serif' }}>
                      События пользователя: {selectedUser.firstName} {selectedUser.lastName}
                    </h3>
                    <div style={{ margin: '20px 0', maxHeight: 300, overflowY: 'auto' }}>
                      {userEvents.map((event) => (
                          <div key={event.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 12, borderBottom: '1px solid #e0e0e0', transition: 'background-color 0.2s' }}>
                            <span>{events.find((e) => e.id === event.eventId)?.eventName}</span>
                            <span>{new Date(event.eventDate).toLocaleDateString("ru-RU")}</span>
                            <button
                                style={{ backgroundColor: '#ff4444', color: 'white', borderRadius: 4, padding: '6px 12px', fontSize: '0.9rem' }}
                                onClick={() => handleDeleteUserEvent(event.id)}
                            >
                              Удалить
                            </button>
                          </div>
                      ))}
                    </div>
                    <div style={{ marginTop: 20 }}>
                      {/* Поле поиска событий */}
                      <input
                          type="text"
                          placeholder="Поиск событий..."
                          value={eventSearch}
                          onChange={(e) => setEventSearch(e.target.value)}
                          style={{ width: '100%', padding: 10, border: '2px solid #e0e0e0', borderRadius: 8, fontSize: 16, marginBottom: 10 }}
                      />

                      {/* Результаты поиска событий */}
                      {foundEvents.length > 0 && (
                          <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 8, margin: '10px 0' }}>
                            {foundEvents.map((event) => (
                                <div
                                    key={event.id}
                                    style={{
                                      padding: 10,
                                      cursor: 'pointer',
                                      transition: 'background 0.2s',
                                      backgroundColor: selectedEventId === event.id ? '#e3f2fd' : 'white',
                                      borderLeft: selectedEventId === event.id ? '4px solid #2196F3' : '4px solid transparent'
                                    }}
                                    onClick={() => handleEventSelect(event)}
                                >
                                  {event.eventName}
                                </div>
                            ))}
                          </div>
                      )}

                      {/* Выбранное событие */}
                      {selectedEventName && (
                          <div style={{
                            padding: 12,
                            backgroundColor: '#e8f5e8',
                            border: '2px solid #4caf50',
                            borderRadius: 8,
                            margin: '10px 0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                      <span style={{ fontWeight: 'bold', color: '#2e7d32' }}>
                        Выбрано: {selectedEventName}
                      </span>
                            <button
                                onClick={handleClearEventSelection}
                                style={{
                                  backgroundColor: '#f44336',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: 4,
                                  padding: '4px 8px',
                                  fontSize: '12px',
                                  cursor: 'pointer'
                                }}
                            >
                              Отменить
                            </button>
                          </div>
                      )}

                      {/* Поле даты */}
                      <input
                          type="date"
                          value={userEventDate}
                          onChange={(e) => setUserEventDate(e.target.value)}
                          style={{ width: '100%', padding: 10, border: '2px solid #e0e0e0', borderRadius: 8, fontSize: 16, marginBottom: 10 }}
                          placeholder="Выберите дату события"
                      />

                      {/* Кнопка добавления */}
                      <button
                          style={{
                            backgroundColor: selectedEventId && userEventDate ? 'rgb(151, 1, 14)' : '#ccc',
                            color: 'white',
                            borderRadius: 4,
                            fontWeight: 500,
                            padding: '0.8rem 1.5rem',
                            fontSize: '1rem',
                            marginTop: 8,
                            cursor: selectedEventId && userEventDate ? 'pointer' : 'not-allowed'
                          }}
                          onClick={handleAddEventToUser}
                          disabled={!selectedEventId || !userEventDate}
                      >
                        Добавить событие
                      </button>
                    </div>
                    <button
                        style={{ marginTop: 20, padding: '10px 20px', background: '#3498db', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer' }}
                        onClick={handleCloseModal}
                    >
                      Закрыть
                    </button>
                  </div>
                </div>
            )}
            {errorMessage && (
                <div style={{ background: '#ffe3e3', color: '#d32f2f', padding: 15, borderRadius: 8, marginTop: 20, textAlign: 'center' }}>{errorMessage}</div>
            )}
          </div>
        </div>
      </div>
  );
};

export default Events;